/**
 * Swappable A2UI boundary: the ONLY module importing `@a2ui/react` / `@a2ui/web_core`.
 * Renders accumulated A2UI spec messages through a catalog mapped to shadcn components.
 */
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useId,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeA2uiComponent, isA2uiActionValid, resolveChoicePresentation } from '../../utils/a2ui-components';
import { Catalog, CommonSchemas, MessageProcessor } from '@a2ui/web_core/v0_9';
import type { A2uiClientAction, ComponentContext, SurfaceModel } from '@a2ui/web_core/v0_9';
import { CalendarIcon } from 'lucide-react';
import {
  A2uiSurface as A2uiSurfaceRenderer,
  createComponentImplementation,
} from '@a2ui/react/v0_9';
import type { ReactComponentImplementation } from '@a2ui/react/v0_9';
import * as RechartsPrimitive from 'recharts';

import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import type { ChartConfig } from 'components/ui/chart';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Separator } from 'components/ui/separator';
import { Slider } from 'components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import type { MessageA2uiSurface } from 'features/chat/types/chat';

export const A2UI_CATALOG_ID = 'selise-chat';
const UNKNOWN_COMPONENT = 'Unknown';
const CHART_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#9333ea', '#0891b2'];

/** Action forwarded to the caller; submit wiring lands in a later stage. */
export interface A2uiAction {
  name: string;
  surfaceId: string;
  sourceComponentId?: string;
  context?: Record<string, unknown>;
}

/** Non-"active" surfaces render read-only (disabled controls). */
const A2uiActiveContext = createContext(true);
const useA2uiDisabled = () => !useContext(A2uiActiveContext);

/** TextField regexp validity per surface; Buttons stay disabled while any field is invalid. */
const A2uiValidityContext = createContext<{
  invalid: ReadonlySet<string>;
  setValid: (id: string, valid: boolean) => void;
}>({ invalid: new Set(), setValid: () => undefined });

const JUSTIFY_CLASS: Record<string, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  spaceBetween: 'justify-between',
  around: 'justify-around',
  spaceAround: 'justify-around',
  evenly: 'justify-evenly',
  spaceEvenly: 'justify-evenly',
};

const ALIGN_CLASS: Record<string, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

/** Universal `weight` prop, read from the raw child model and applied as flex-grow. */
const childWeight = (context: ComponentContext, id: string): number | undefined => {
  const weight = context.surfaceComponents.get(id)?.properties.weight;
  return typeof weight === 'number' && weight > 0 ? weight : undefined;
};

const childIds = (children: unknown): string[] => {
  if (Array.isArray(children)) return children.filter((id): id is string => typeof id === 'string');
  if (children != null) console.warn('[A2UI] unsupported child list form:', children);
  return [];
};

const asRows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(
        (row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row)
      )
    : [];

const cellText = (value: unknown): string =>
  value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);

const A2uiText = createComponentImplementation(
  {
    name: 'Text',
    schema: z.object({
      text: CommonSchemas.DynamicString.optional(),
      usageHint: z.string().optional(),
    }),
  },
  ({ props }) => {
    const text = props.text ?? '';
    switch (props.usageHint) {
      case 'h1':
        return <h3 className="text-xl font-bold mb-1">{text}</h3>;
      case 'h2':
        return <h4 className="text-lg font-semibold mb-1">{text}</h4>;
      case 'h3':
        return <h5 className="text-base font-semibold mb-1">{text}</h5>;
      case 'caption':
        return <p className="text-xs text-muted-foreground">{text}</p>;
      case 'h4':
        return <h4 className="text-sm font-semibold">{text}</h4>;
      case 'h5':
        return <h5 className="text-sm font-medium">{text}</h5>;
      default:
        return (
          <div className="text-sm prose dark:prose-invert">
            <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
              {text}
            </ReactMarkdown>
          </div>
        );
    }
  }
);

const A2uiRow = createComponentImplementation(
  {
    name: 'Row',
    schema: z.object({
      children: CommonSchemas.ChildList.optional(),
      justify: z.string().optional(),
      align: z.string().optional(),
    }),
  },
  ({ props, buildChild, context }) => (
    <div
      className={`flex flex-row flex-wrap gap-2 ${JUSTIFY_CLASS[props.justify ?? ''] ?? 'justify-start'} ${ALIGN_CLASS[props.align ?? ''] ?? 'items-start'}`}
    >
      {childIds(props.children).map((id) => {
        const weight = childWeight(context, id);
        return (
          <div
            key={id}
            className={weight == null ? 'min-w-0 flex-1' : 'min-w-0'}
            style={weight == null ? undefined : { flexGrow: weight, flexBasis: 0 }}
          >
            {context.surfaceComponents.get(id) ? buildChild(id) : null}
          </div>
        );
      })}
    </div>
  )
);

const A2uiColumn = createComponentImplementation(
  {
    name: 'Column',
    schema: z.object({
      children: CommonSchemas.ChildList.optional(),
      justify: z.string().optional(),
      align: z.string().optional(),
    }),
  },
  ({ props, buildChild, context }) => (
    <div
      className={`flex flex-col gap-2 ${JUSTIFY_CLASS[props.justify ?? ''] ?? 'justify-start'} ${ALIGN_CLASS[props.align ?? ''] ?? 'items-stretch'}`}
    >
      {childIds(props.children).map((id) => {
        const weight = childWeight(context, id);
        return (
          <div
            key={id}
            className="min-w-0"
            style={weight == null ? undefined : { flexGrow: weight }}
          >
            {context.surfaceComponents.get(id) ? buildChild(id) : null}
          </div>
        );
      })}
    </div>
  )
);

const A2uiCard = createComponentImplementation(
  {
    name: 'Card',
    schema: z.object({
      title: CommonSchemas.DynamicString.optional(),
      child: z.string().optional(),
      children: CommonSchemas.ChildList.optional(),
    }),
  },
  ({ props, buildChild, context }) => {
    const ids = props.child ? [props.child] : childIds(props.children);
    return (
      <Card>
        {props.title && (
          <CardHeader className="p-4 pb-0">
            <CardTitle className="text-base">{props.title}</CardTitle>
          </CardHeader>
        )}
        <CardContent className="flex flex-col gap-2 p-4">
          {ids.map((id) => (
            <div key={id} className="min-w-0">
              {context.surfaceComponents.get(id) ? buildChild(id) : null}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
);

const A2uiButton = createComponentImplementation(
  {
    name: 'Button',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      child: z.string().optional(),
      action: CommonSchemas.Action.optional(),
      variant: z.string().optional(),
      disabled: CommonSchemas.DynamicBoolean.optional(),
    }),
  },
  ({ props, context, buildChild }) => {
    const readOnly = useA2uiDisabled();
    const [, refresh] = useState(0);
    const action = context.componentModel.properties.action as any;
    const bindings = useMemo(() => action?.event?.context ?? {}, [action]);
    const fields = [...context.surfaceComponents.entries].map(([id, model]) => ({
      id,
      component: model.type,
      ...model.properties,
    }));
    const valid = () =>
      isA2uiActionValid(
        fields,
        Object.fromEntries(
          Object.entries(bindings).map(([key, binding]) => [
            key,
            context.dataContext.resolveDynamicValue(binding as any),
          ])
        ),
        bindings
      );
    useEffect(() => {
      const subscriptions = Object.values(bindings).map((binding) =>
        context.dataContext.subscribeDynamicValue(binding as any, () => refresh((n) => n + 1))
      );
      return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    }, [context, bindings]);
    const variant =
      props.variant === 'secondary' || props.variant === 'outline' || props.variant === 'ghost'
        ? props.variant
        : props.variant === 'destructive'
          ? 'destructive'
          : 'default';
    return (
      <Button
        type="button"
        size="sm"
        variant={variant}
        disabled={readOnly || props.disabled === true || !valid()}
        onClick={() => {
          if (!readOnly && valid()) props.action?.();
        }}
      >
        {props.label ??
          (props.child && context.surfaceComponents.get(props.child)
            ? buildChild(props.child)
            : '')}
      </Button>
    );
  }
);

const A2uiTextField = createComponentImplementation(
  {
    name: 'TextField',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      text: CommonSchemas.DynamicString.optional(),
      placeholder: z.string().optional(),
      validationRegexp: z.string().optional(),
    }),
  },
  ({ props, context }) => {
    const readOnly = useA2uiDisabled();
    const { setValid } = useContext(A2uiValidityContext);
    const componentId = context.componentModel.id;
    const inputId = `a2ui-${componentId}`;
    const value = props.text ?? '';
    const invalid = useMemo(() => {
      if (!props.validationRegexp || !value) return false;
      try {
        return !new RegExp(props.validationRegexp).test(value);
      } catch {
        return false;
      }
    }, [props.validationRegexp, value]);
    useEffect(() => {
      setValid(componentId, !invalid);
      return () => setValid(componentId, true);
    }, [setValid, componentId, invalid]);
    return (
      <div className="flex flex-col gap-1.5">
        {props.label && <Label htmlFor={inputId}>{props.label}</Label>}
        <Input
          id={inputId}
          value={value}
          placeholder={props.placeholder}
          disabled={readOnly}
          aria-invalid={invalid || undefined}
          className={invalid ? 'border-destructive focus-visible:ring-destructive' : undefined}
          onChange={(event) => props.setText?.(event.target.value)}
        />
      </div>
    );
  }
);

const OptionSchema = z.object({
  label: z.string().optional(),
  description: z.string().optional(),
  value: z.string(),
});

const A2uiSelect = createComponentImplementation(
  {
    name: 'Select',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      options: z.array(OptionSchema).optional(),
      value: CommonSchemas.DynamicString.optional(),
      placeholder: z.string().optional(),
    }),
  },
  ({ props }) => {
    const readOnly = useA2uiDisabled();
    const options = props.options ?? [];
    return (
      <div className="flex flex-col gap-1.5">
        {props.label && <Label>{props.label}</Label>}
        <Select
          value={props.value || undefined}
          disabled={readOnly}
          onValueChange={(value) => props.setValue?.(value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={props.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label ?? option.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
);

const A2uiMultiSelect = createComponentImplementation(
  {
    name: 'MultiSelect',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      options: z.array(OptionSchema).optional(),
      values: CommonSchemas.DynamicValue.optional(),
    }),
  },
  ({ props, context }) => {
    const readOnly = useA2uiDisabled();
    const options = props.options ?? [];
    const selected = Array.isArray(props.values) ? props.values.map(String) : [];
    const toggle = (value: string, checked: boolean) => {
      const next = checked ? [...selected, value] : selected.filter((v) => v !== value);
      props.setValues?.(next);
    };
    return (
      <div className="flex flex-col gap-1.5">
        {props.label && <Label>{props.label}</Label>}
        <div className="flex flex-col gap-2">
          {options.map((option) => {
            const checkboxId = `a2ui-${context.componentModel.id}-${option.value}`;
            return (
              <div key={option.value} className="flex items-center gap-2">
                <Checkbox
                  id={checkboxId}
                  checked={selected.includes(option.value)}
                  disabled={readOnly}
                  onCheckedChange={(checked) => toggle(option.value, checked === true)}
                />
                <Label htmlFor={checkboxId} className="font-normal">
                  {option.label ?? option.value}
                </Label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

const A2uiCheckBox = createComponentImplementation(
  {
    name: 'CheckBox',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      value: CommonSchemas.DynamicBoolean.optional(),
    }),
  },
  ({ props, context }) => {
    const readOnly = useA2uiDisabled();
    const checkboxId = `a2ui-${context.componentModel.id}`;
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={props.value === true}
          disabled={readOnly}
          onCheckedChange={(checked) => props.setValue?.(checked === true)}
        />
        {props.label && (
          <Label htmlFor={checkboxId} className="font-normal">
            {props.label}
          </Label>
        )}
      </div>
    );
  }
);

const pad2 = (value: number) => String(value).padStart(2, '0');
const isoDate = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const A2uiDateTimeInput = createComponentImplementation(
  {
    name: 'DateTimeInput',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      value: CommonSchemas.DynamicString.optional(),
      enableDate: z.boolean().optional(),
      enableTime: z.boolean().optional(),
    }),
  },
  ({ props }) => {
    const readOnly = useA2uiDisabled();
    const enableDate = props.enableDate !== false;
    const enableTime = props.enableTime === true;
    const raw = typeof props.value === 'string' ? props.value : '';
    const dateText = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : '';
    const timeText = /T(\d{2}:\d{2})/.exec(raw)?.[1] ?? (/^\d{2}:\d{2}$/.test(raw) ? raw : '');
    // Bound value stays ISO-8601: date-only while time is off, else local date-time.
    const commit = (date: string, time: string) => {
      if (!enableDate) return props.setValue?.(time);
      if (!date) return props.setValue?.('');
      props.setValue?.(enableTime ? `${date}T${time || '00:00'}:00` : date);
    };
    return (
      <div className="flex flex-col gap-1.5">
        {props.label && <Label>{props.label}</Label>}
        <div className="flex flex-wrap items-center gap-2">
          {enableDate && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={readOnly}
                  className={`justify-start font-normal ${dateText ? '' : 'text-muted-foreground'}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateText || 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateText ? new Date(`${dateText}T00:00:00`) : undefined}
                  onSelect={(day) => day && commit(isoDate(day), timeText)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          )}
          {enableTime && (
            <Input
              type="time"
              value={timeText}
              disabled={readOnly}
              className="w-32"
              onChange={(event) => commit(dateText, event.target.value)}
            />
          )}
        </div>
      </div>
    );
  }
);

const A2uiSlider = createComponentImplementation(
  {
    name: 'Slider',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      value: CommonSchemas.DynamicNumber.optional(),
      minValue: z.number().optional(),
      maxValue: z.number().optional(),
      step: z.number().optional(),
    }),
  },
  ({ props }) => {
    const readOnly = useA2uiDisabled();
    const min = props.minValue ?? 0;
    const max = props.maxValue ?? 100;
    const value = typeof props.value === 'number' ? props.value : min;
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          {props.label && <Label>{props.label}</Label>}
          <span className="ml-auto text-xs text-muted-foreground">{value}</span>
        </div>
        <Slider
          value={[value]}
          min={min}
          max={max}
          step={props.step}
          disabled={readOnly}
          onValueChange={([next]) => props.setValue?.(next)}
        />
      </div>
    );
  }
);

const A2uiDivider = createComponentImplementation(
  {
    name: 'Divider',
    schema: z.object({ axis: z.string().optional() }),
  },
  ({ props }) =>
    props.axis === 'vertical' ? (
      <Separator orientation="vertical" className="mx-1 self-stretch" />
    ) : (
      <Separator className="my-1" />
    )
);

const A2uiChoicePicker = createComponentImplementation(
  {
    name: 'ChoicePicker',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      options: z.array(OptionSchema).optional(),
      selections: CommonSchemas.DynamicValue.optional(),
      maxAllowedSelections: z.number().optional(),
      minAllowedSelections: z.number().optional(),
      presentation: z.enum(['auto', 'radio', 'checkbox', 'dropdown', 'cards']).optional(),
      placeholder: z.string().optional(),
    }),
  },
  ({ props }) => {
    const readOnly = useA2uiDisabled();
    const groupId = useId();
    const { t } = useTranslation();
    const options = props.options ?? [];
    const selected = Array.isArray(props.selections) ? props.selections.map(String) : [];
    const single = props.maxAllowedSelections === 1;
    const presentation = resolveChoicePresentation(props.presentation, props.maxAllowedSelections, options.length);
    const label = props.label || t('A2UI_CHOICES', 'Options');
    const labelId = `${groupId}-label`;
    const update = (value: string, checked: boolean) => {
      if (readOnly) return;
      const next = single ? [value] : checked ? [...new Set([...selected, value])] : selected.filter((v) => v !== value);
      if (props.maxAllowedSelections != null && next.length > props.maxAllowedSelections) return;
      props.setSelections?.(next);
    };
    if (presentation === 'dropdown') {
      return (
        <div className="flex flex-col gap-1.5">
          <Label id={labelId} htmlFor={groupId}>{label}</Label>
          <Select value={selected[0] || undefined} disabled={readOnly} onValueChange={(value) => update(value, true)}>
            <SelectTrigger id={groupId} aria-labelledby={labelId} className="min-h-11">
              <SelectValue placeholder={props.placeholder || t('A2UI_SELECT_OPTION', 'Select an option')}>
                {options.find((option) => option.value === selected[0])?.label || selected[0]}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="block break-words">{option.label ?? option.value}</span>
                  {option.description && <span className="block text-xs text-muted-foreground">{option.description}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    const cards = presentation === 'cards';
    const rows = options.map((option, index) => {
      const id = `${groupId}-${index}`;
      const checked = selected.includes(option.value);
      const disabled = readOnly || (!single && !checked && props.maxAllowedSelections != null && selected.length >= props.maxAllowedSelections);
      const controlProps = { id, disabled, 'aria-labelledby': `${id}-text`, 'aria-describedby': option.description ? `${id}-description` : undefined };
      return (
        <Label
          key={option.value}
          htmlFor={id}
          className={`flex min-h-11 min-w-0 items-start gap-3 rounded-md border p-3 font-normal transition-colors focus-within:ring-2 focus-within:ring-ring ${checked ? 'border-primary bg-primary/5' : 'border-border'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/50'}`}
        >
          {single ? <RadioGroupItem {...controlProps} value={option.value} className="mt-0.5 shrink-0" /> :
            <Checkbox {...controlProps} checked={checked} className="mt-0.5 shrink-0" onCheckedChange={(value) => update(option.value, value === true)} />}
          <span className="min-w-0 break-words">
            <span id={`${id}-text`} className={cards ? 'font-medium' : undefined}>{option.label ?? option.value}</span>
            {option.description && <span id={`${id}-description`} className="mt-1 block text-sm text-muted-foreground">{option.description}</span>}
          </span>
        </Label>
      );
    });
    const layout = cards ? 'grid grid-cols-1 gap-2 sm:grid-cols-2' : 'grid grid-cols-1 gap-2';
    return (
      <div className="flex min-w-0 flex-col gap-2" data-choice-presentation={presentation}>
        <Label id={labelId}>{label}</Label>
        {single ? <RadioGroup aria-labelledby={labelId} value={selected[0] ?? ''} disabled={readOnly} onValueChange={(value) => update(value, true)} className={layout}>{rows}</RadioGroup> :
          <div role="group" aria-labelledby={labelId} className={layout}>{rows}</div>}
      </div>
    );
  }
);

const A2uiPages = ({
  page,
  size,
  total,
  onPage,
}: {
  page: number;
  size: number;
  total: number;
  onPage: (page: number) => void;
}) => {
  const { t } = useTranslation();
  if (total <= size) return null;
  return (
    <div className="flex items-center gap-2 py-2 text-xs">
      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPage(page - 1)}>
        {t('PREVIOUS', 'Previous')}
      </Button>
      <span>
        {page * size + 1}–{Math.min((page + 1) * size, total)} {t('OF', 'of')} {total}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={(page + 1) * size >= total}
        onClick={() => onPage(page + 1)}
      >
        {t('NEXT', 'Next')}
      </Button>
    </div>
  );
};

const A2uiList = createComponentImplementation(
  {
    name: 'List',
    schema: z.object({
      children: CommonSchemas.ChildList,
      direction: z.enum(['vertical', 'horizontal']).optional(),
      pageSize: z.number().int().positive().optional(),
    }),
  },
  ({ props, context, buildChild }) => {
    const children = Array.isArray(props.children) ? props.children : [];
    const [page, setPage] = useState(0);
    const size = Math.min(props.pageSize ?? 25, 200);
    const current = Math.min(page, Math.max(0, Math.ceil(children.length / size) - 1));
    return (
      <div>
        <div
          className={`flex gap-2 ${props.direction === 'horizontal' ? 'flex-row flex-wrap' : 'flex-col'}`}
        >
          {children.slice(current * size, (current + 1) * size).map((child: any, index: number) => {
            // web_core resolves STRUCTURAL templates to {id, basePath}; preserve that item scope.
            const id = typeof child === 'string' ? child : child.id;
            return context.surfaceComponents.get(id) ? (
              <div key={`${id}-${child.basePath ?? index}`}>{buildChild(id, child.basePath)}</div>
            ) : null;
          })}
        </div>
        <A2uiPages page={current} size={size} total={children.length} onPage={setPage} />
      </div>
    );
  }
);

const A2uiTable = createComponentImplementation(
  {
    name: 'Table',
    schema: z.object({
      columns: z
        .array(z.object({ header: z.string().optional(), field: z.string() }))
        .optional(),
      rows: CommonSchemas.DynamicValue.optional(),
      pageSize: z.number().int().positive().optional(),
    }),
  },
  ({ props }) => {
    const rows = asRows(props.rows);
    const [page, setPage] = useState(0);
    const size = Math.min(props.pageSize ?? 25, 200);
    const current = Math.min(page, Math.max(0, Math.ceil(rows.length / size) - 1));
    const columns =
      props.columns ?? Object.keys(rows[0] ?? {}).map((field) => ({ field, header: field }));
    if (columns.length === 0) return null;
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.field}>{column.header ?? column.field}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(current * size, (current + 1) * size).map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column) => (
                  <TableCell key={column.field}>{cellText(row[column.field])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <A2uiPages page={current} size={size} total={rows.length} onPage={setPage} />
      </div>
    );
  }
);

const A2uiChart = createComponentImplementation(
  {
    name: 'Chart',
    schema: z.object({
      chartType: z.string().optional(),
      data: CommonSchemas.DynamicValue.optional(),
      xKey: z.string().optional(),
      series: z
        .array(z.object({ key: z.string(), label: z.string().optional(), color: z.string().optional() }))
        .optional(),
    }),
  },
  ({ props }) => {
    const data = asRows(props.data);
    if (data.length === 0) return null;

    const xKey = props.xKey ?? Object.keys(data[0])[0];
    const series =
      props.series ??
      Object.keys(data[0])
        .filter((key) => key !== xKey && typeof data[0][key] === 'number')
        .map((key) => ({ key, label: key, color: undefined as string | undefined }));
    if (series.length === 0) return null;

    const config: ChartConfig = Object.fromEntries(
      series.map((entry, index) => [
        entry.key,
        {
          label: entry.label ?? entry.key,
          color: entry.color ?? CHART_COLORS[index % CHART_COLORS.length],
        },
      ])
    );
    const chartType = props.chartType ?? 'bar';

    if (chartType === 'pie') {
      const pieData = data.map((row, index) => ({
        name: cellText(row[xKey]),
        value: Number(row[series[0].key] ?? 0),
        fill: CHART_COLORS[index % CHART_COLORS.length],
      }));
      return (
        <ChartContainer config={config} className="max-h-64 w-full">
          <RechartsPrimitive.PieChart>
            <ChartTooltip content={<ChartTooltipContent />} />
            <RechartsPrimitive.Pie data={pieData} dataKey="value" nameKey="name" />
          </RechartsPrimitive.PieChart>
        </ChartContainer>
      );
    }

    const isLine = chartType === 'line';
    const isArea = chartType === 'area';
    const ChartComponent = isArea
      ? RechartsPrimitive.AreaChart
      : isLine
        ? RechartsPrimitive.LineChart
        : RechartsPrimitive.BarChart;
    return (
      <ChartContainer config={config} className="max-h-64 w-full">
        <ChartComponent data={data}>
          <RechartsPrimitive.CartesianGrid vertical={false} />
          <RechartsPrimitive.XAxis dataKey={xKey} tickLine={false} axisLine={false} />
          <RechartsPrimitive.YAxis tickLine={false} axisLine={false} width={40} />
          <ChartTooltip content={<ChartTooltipContent />} />
          {series.map((entry) =>
            isArea ? (
              <RechartsPrimitive.Area
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                stroke={config[entry.key].color}
                fill={config[entry.key].color}
                fillOpacity={0.2}
              />
            ) : isLine ? (
              <RechartsPrimitive.Line
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                stroke={config[entry.key].color}
                strokeWidth={2}
                dot={false}
              />
            ) : (
              <RechartsPrimitive.Bar
                key={entry.key}
                dataKey={entry.key}
                fill={config[entry.key].color}
                radius={4}
              />
            )
          )}
        </ChartComponent>
      </ChartContainer>
    );
  }
);

const A2uiStat = createComponentImplementation(
  {
    name: 'Stat',
    schema: z.object({
      label: CommonSchemas.DynamicString.optional(),
      value: CommonSchemas.DynamicValue.optional(),
      delta: CommonSchemas.DynamicString.optional(),
    }),
  },
  ({ props }) => (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        {props.label && <p className="text-xs text-muted-foreground">{props.label}</p>}
        <p className="text-2xl font-semibold">{cellText(props.value)}</p>
        {props.delta && <p className="text-xs text-muted-foreground">{props.delta}</p>}
      </CardContent>
    </Card>
  )
);

/** Sanitizer target for unknown component types: render nothing, never crash. */
const A2uiUnknown = createComponentImplementation(
  { name: UNKNOWN_COMPONENT, schema: z.object({}).passthrough() },
  ({ context }) => context.componentModel.id === 'root' ? <A2uiFailure /> : null
);

const chatCatalog = new Catalog<ReactComponentImplementation>(A2UI_CATALOG_ID, [
  A2uiText,
  A2uiRow,
  A2uiColumn,
  A2uiCard,
  A2uiButton,
  A2uiTextField,
  A2uiSelect,
  A2uiMultiSelect,
  A2uiCheckBox,
  A2uiDateTimeInput,
  A2uiSlider,
  A2uiDivider,
  A2uiChoicePicker,
  A2uiTable,
  A2uiList,
  A2uiChart,
  A2uiStat,
  A2uiUnknown,
]);

const SPEC_MESSAGE_KEYS = ['createSurface', 'updateComponents', 'updateDataModel', 'deleteSurface'];

/** Pins surfaceId/catalogId to this surface's identity and no-ops unknown component types. */
function sanitizeSpecMessage(
  raw: Record<string, unknown>,
  surfaceId: string
): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  if (!SPEC_MESSAGE_KEYS.some((key) => key in raw)) {
    console.warn('[A2UI] ignoring unsupported message:', Object.keys(raw));
    return null;
  }
  const message = { ...raw };
  for (const key of SPEC_MESSAGE_KEYS) {
    const payload = message[key] as Record<string, unknown> | undefined;
    if (payload && typeof payload === 'object') {
      // The processor routes (and silently drops) messages by inner surfaceId.
      message[key] = { ...payload, surfaceId };
    }
  }
  const createSurface = message.createSurface as Record<string, unknown> | undefined;
  if (createSurface) {
    message.createSurface = { ...createSurface, catalogId: A2UI_CATALOG_ID };
  }
  const updateComponents = message.updateComponents as { components?: unknown[] } | undefined;
  if (updateComponents && Array.isArray(updateComponents.components)) {
    message.updateComponents = {
      ...updateComponents,
      components: updateComponents.components.map((entry) => {
        const component = entry as { component?: string } | null;
        if (component?.component && !chatCatalog.components.has(component.component)) {
          console.warn(`[A2UI] unknown component type: ${component.component}`);
          return { ...component, component: UNKNOWN_COMPONENT };
        }
        return normalizeA2uiComponent(entry as Record<string, any>);
      }),
    };
  }
  return message;
}

interface A2UISurfaceProps {
  surface: MessageA2uiSurface;
  onAction?: (action: A2uiAction) => void;
  readOnly?: boolean;
}

const A2UISurfaceContent = memo(({ surface, onAction, readOnly }: A2UISurfaceProps) => {
  const [processingError, setProcessingError] = useState(false);
  const [processedSurfaceId, setProcessedSurfaceId] = useState<string | null>(null);
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;

  const processorRef = useRef<MessageProcessor<ReactComponentImplementation> | null>(null);
  const processedRef = useRef<Record<string, unknown>[]>([]);
  const [surfaceModel, setSurfaceModel] =
    useState<SurfaceModel<ReactComponentImplementation> | null>(null);
  const [, setVersion] = useState(0);

  const [invalidFields, setInvalidFields] = useState<ReadonlySet<string>>(() => new Set());
  const setFieldValid = useCallback((id: string, valid: boolean) => {
    setInvalidFields((previous) => {
      if (previous.has(id) !== valid) return previous;
      const next = new Set(previous);
      if (valid) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const validity = useMemo(
    () => ({ invalid: invalidFields, setValid: setFieldValid }),
    [invalidFields, setFieldValid]
  );

  useEffect(() => {
    setProcessingError(false);
    const previous = processedRef.current;
    const next = surface.messages;
    const isAppend =
      processorRef.current != null &&
      next.length >= previous.length &&
      previous.every((message, index) => next[index] === message);

    if (!isAppend) {
      processorRef.current = new MessageProcessor<ReactComponentImplementation>(
        [chatCatalog],
        (clientAction: A2uiClientAction) => {
          onActionRef.current?.({
            name: clientAction.name,
            surfaceId: surface.surfaceId,
            sourceComponentId: clientAction.sourceComponentId,
            context: clientAction.context,
          });
        }
      );
      processedRef.current = [];
    }

    const processor = processorRef.current;
    if (!processor) return;

    const pending = next.slice(processedRef.current.length);
    processedRef.current = next;
    for (const raw of pending) {
      try {
        const sanitized = sanitizeSpecMessage(raw, surface.surfaceId);
        if (!sanitized) throw new Error('Unsupported surface message');
        processor.processMessages([sanitized] as never);
      } catch (error) {
        console.warn('[A2UI] dropped message:', error);
        setProcessingError(true);
      }
    }

    setSurfaceModel(processor.model.surfacesMap.values().next().value ?? null);
    setProcessedSurfaceId(surface.surfaceId);
    setVersion((version) => version + 1);
  }, [surface.surfaceId, surface.messages]);

  if (processedSurfaceId !== surface.surfaceId) return null;
  if (processingError) return <A2uiFailure />;
  if (!surfaceModel || !surfaceModel.componentsModel.get('root'))
    return surface.messages.length ? <A2uiFailure /> : null;

  return (
    <A2uiActiveContext.Provider value={!readOnly && surface.status === 'active'}>
      <A2uiValidityContext.Provider value={validity}>
        <div
          className="a2ui-surface my-2 w-full min-w-0 max-w-2xl"
          data-surface-id={surface.surfaceId}
          data-status={surface.status}
        >
          <A2uiSurfaceRenderer surface={surfaceModel} />
        </div>
      </A2uiValidityContext.Provider>
    </A2uiActiveContext.Provider>
  );
});

A2UISurfaceContent.displayName = 'A2UISurfaceContent';

const A2uiFailure = () => {
  const { t } = useTranslation();
  return (
    <div role="status" className="my-2 rounded-md border p-3 text-sm text-muted-foreground">
      {t('A2UI_RENDER_FAILED', "This interactive element couldn't be displayed.")}
    </div>
  );
};

export const A2UISurface = (props: A2UISurfaceProps) => (
  <ErrorBoundary
    fallback={<A2uiFailure />}
    resetKeys={[props.surface.surfaceId, props.surface.messages]}
  >
    <A2UISurfaceContent {...props} />
  </ErrorBoundary>
);
